import type { Packet } from 'socket.io-parser';

import type { Maybe } from '@/src/types';

import * as BaseParser from 'socket.io-parser';

import { ensureError } from '@/src/utils';

export const CHUNK_SIZE = 1 * 1024 * 1024;
export const CHUNKED_PACKET_TYPE = 'C';

function isBinary(obj: any) {
  return (
    obj instanceof ArrayBuffer
    || ArrayBuffer.isView(obj)
    || obj instanceof Blob
    || obj instanceof File
  );
}

type BinaryEncoded = [ string, ...(ArrayBuffer | ArrayBufferView | Blob | File)[]];
type StringEncoded = [ string ];

type BaseEncoded = StringEncoded | BinaryEncoded;

function hasAttribute<Field extends string>(obj: unknown, field: Field): obj is { [K in Field]: unknown } {
  return typeof obj === 'object' && obj !== null && field in obj;
}

function hasIntegerAttribute<Field extends string>(obj: unknown, field: Field): obj is { [K in Field]: number } {
  return hasAttribute(obj, field) && Number.isInteger(obj[field]);
}

function getLength(obj: unknown): number {
  const objAsObject = new Object(obj);
  if (hasIntegerAttribute(objAsObject, 'byteLength')) return objAsObject.byteLength;
  if (hasIntegerAttribute(objAsObject, 'length')) return objAsObject.length;
  throw new Error(`Cannot get obj length: ${typeof obj}`);
}

function allStringChunks(chunks: any[]): chunks is string[] {
  return chunks.every(chunk => typeof chunk === 'string');
}

function allBinaryChunks(chunks: any[]): chunks is Uint8Array[] {
  return chunks.every(chunk => isBinary(chunk));
}

/**
 * Encodes a binary packet into fixed-sized chunk messages.
 *
 * The chunked encoder extends the default socket.io-parser protocol to support
 * chunked binary attachments. It should work with any protocol version, but has
 * only been tested with v5.
 *
 * This encoding adds an optional first message indicating that all subsequent
 * messages are chunked.
 *
 * [<chunking message>]
 * <socket.io string message>
 * [<binary attachments>...]
 *
 * The chunking message is a string message that starts with the char 'C' and
 * has the following format:
 *
 * `C<chunking info>`
 *
 * The format of <chunking info> is a flat array of integers:
 * [N1, N2, N3, ...]
 *
 * There are a total of M integers, for M messages. The ith integer tells
 * us how many messages should be concatenated together to re-form the ith
 * message.
 *
 * Chunking works on both string and binary messages.
 */
class ChunkedEncoder extends BaseParser.Encoder {
  encode(packet: Packet) {
    const messages = super.encode(packet) as BaseEncoded;

    // All messages are smaller than the chunk size.
    // Skip wrapping the socket.io message with chunking.
    if (messages.every(m => getLength(m) <= CHUNK_SIZE)) {
      return messages;
    }

    type Chunk = ReturnType<typeof this.chunkMessage>[number];
    const output: Chunk[] = [];
    const chunkedSizes: number[] = [];

    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const chunks = this.chunkMessage(msg);
      chunkedSizes.push(chunks.length);
      output.push(...chunks);
    }

    return [`${CHUNKED_PACKET_TYPE}${JSON.stringify(chunkedSizes)}`, ...output];
  }

  protected chunkMessage(msg: BaseEncoded[number]) {
    if (typeof msg === 'string') {
      return this.chunkString(msg);
    }
    if (isBinary(msg)) {
      return this.chunkBinary(msg);
    }
    throw new TypeError('Cannot handle invalid message type');
  }

  protected chunkString(str: string) {
    const chunks: string[] = [];
    let offset = 0;
    while (offset < str.length) {
      const chunkEnd = Math.min(offset + CHUNK_SIZE, str.length);
      chunks.push(str.substring(offset, chunkEnd));
      offset = chunkEnd;
    }
    return chunks;
  }

  protected chunkBinary(binary: ArrayBufferView | ArrayBuffer | Blob | File) {
    if (ArrayBuffer.isView(binary) || binary instanceof ArrayBuffer) {
      const view
        = binary instanceof ArrayBuffer ? new Uint8Array(binary) : binary;
      const chunks: Uint8Array[] = [];
      const { buffer } = view;
      const end = view.byteOffset + view.byteLength;
      let offset = view.byteOffset;
      while (offset < end) {
        const chunkEnd = Math.min(offset + CHUNK_SIZE, end);
        chunks.push(new Uint8Array(buffer, offset, chunkEnd - offset));
        offset = chunkEnd;
      }
      return chunks;
    }

    // Blob | File
    const chunks: Blob[] = [];
    let offset = 0;
    while (offset < binary.size) {
      const chunkEnd = Math.min(offset + CHUNK_SIZE, binary.size);
      chunks.push(binary.slice(offset, chunkEnd));
      offset += chunkEnd;
    }
    return chunks;
  }
}

type ChunkForDecoder
  = ReturnType<typeof ChunkedEncoder.prototype.encode>[number]
    | SharedArrayBuffer;

class ChunkedDecoder extends BaseParser.Decoder {
  protected chunkingInfo: Maybe<number[]>;
  protected chunks: Array<ChunkForDecoder> = [];

  add(obj: ChunkForDecoder): void {
    if (this.chunkingInfo?.length) {
      // process chunk
      this.chunks.push(obj);

      if (this.chunks.length === this.chunkingInfo[0]) {
        super.add(this.reconstructChunks(this.chunks));
        this.chunks.length = 0;
        this.chunkingInfo.shift();
      }

      if (this.chunkingInfo.length === 0) {
        // reset chunking state
        this.chunkingInfo = null;
      }
    }
    else if (
      typeof obj === 'string'
      && obj.charAt(0) === CHUNKED_PACKET_TYPE
    ) {
      // chunking message
      if (obj.charAt(1) !== '[') {
        throw new Error('Failed to parse start of chunking info.');
      }
      this.chunkingInfo = this.parseChunkingInfo(obj.substring(1));
      this.chunks = [];
    }
    else {
      // let the parent take care of it.
      super.add(obj);
    }
  }

  protected parseChunkingInfo(serialized: string) {
    try {
      const result = JSON.parse(serialized) as number[];
      if (!Array.isArray(result)) {
        throw new TypeError('Chunking info is not an array');
      }
      if (!result.every(item => Number.isInteger(item))) {
        throw new TypeError('Chunking info is invalid');
      }
      return result;
    }
    catch (err) {
      throw new Error('Failed to parse chunking info', {
        cause: ensureError(err),
      });
    }
  }

  protected reconstructChunks(chunks: Array<ChunkForDecoder>) {
    if (!chunks.length) {
      throw new Error('Cannot reconstruct an empty chunk');
    }

    if (allStringChunks(chunks)) {
      return this.reconstructString(chunks);
    }
    if (allBinaryChunks(chunks)) {
      return this.reconstructBinary(chunks);
    }

    throw new TypeError('Received a set of unknown chunks');
  }

  protected reconstructString(chunks: string[]) {
    return chunks.join('');
  }

  protected reconstructBinary(chunks: Uint8Array[]) {
    const byteLength = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const reconstructed = new Uint8Array(byteLength);
    let offset = 0;
    chunks.forEach((chunk) => {
      reconstructed.set(new Uint8Array(chunk), offset);
      offset += chunk.byteLength;
    });
    return reconstructed.buffer;
  }
}

export const Encoder = ChunkedEncoder;
export const Decoder = ChunkedDecoder;
